import React, { useState } from "react";
import {
  Box,
  IconButton,
  Typography,
  TextField,
  Select,
  MenuItem,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";

interface PaginationProps {
  page: number;
  rowsPerPage: number;
  totalItems: number;
  onPageChange: (newPage: number) => void;
  onRowsPerPageChange: (newRowsPerPage: number) => void;
  rowsPerPageOptions?: number[];
}

export const Pagination: React.FC<PaginationProps> = ({
  page,
  rowsPerPage,
  totalItems,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = [25, 50, 100],
}) => {
  const [pageInput, setPageInput] = useState("");
  const totalPages = Math.max(1, Math.ceil(totalItems / rowsPerPage));

  const handlePageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPageInput(e.target.value);
  };

  const handlePageInputSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const pageNum = parseInt(pageInput, 10);
    if (pageNum >= 1 && pageNum <= totalPages) {
      onPageChange(pageNum - 1);
      setPageInput("");
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        justifyContent: "flex-end",
        p: 0.5,
        flexWrap: "nowrap",
      }}
    >
      <Typography variant="body2" color="text.secondary">
        Rows:
      </Typography>
      <Select
        size="small"
        value={rowsPerPage}
        onChange={(e) => onRowsPerPageChange(Number(e.target.value))}
        sx={{ minWidth: 60 }}
      >
        {rowsPerPageOptions.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </Select>

      <IconButton
        size="small"
        onClick={() => onPageChange(page - 1)}
        disabled={page === 0}
      >
        <ArrowBackIcon fontSize="small" />
      </IconButton>

      <Box
        sx={{
          minWidth: "fit-content",
          whiteSpace: "nowrap",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Page {page + 1} of {totalPages}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {totalItems.toLocaleString()} Total Records
        </Typography>
      </Box>

      <IconButton
        size="small"
        onClick={() => onPageChange(page + 1)}
        disabled={page >= totalPages - 1}
      >
        <ArrowForwardIcon fontSize="small" />
      </IconButton>

      <Box
        component="form"
        onSubmit={handlePageInputSubmit}
        sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: 0.5 }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ whiteSpace: "nowrap" }}
        >
          Go to:
        </Typography>
        <TextField
          size="small"
          type="number"
          value={pageInput}
          onChange={handlePageInputChange}
          placeholder="#"
          sx={{ width: 52 }}
          inputProps={{
            min: 1,
            max: totalPages,
            style: { padding: "4px 8px" },
          }}
        />
      </Box>
    </Box>
  );
};
